import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTemplateContext } from '../components/ui/composed/template/TemplateContext';
import useDeclarations from './useDeclarations';
import { AISMessageTypes, CDSExportMessageTypes } from 'store/declarations/enums/common/declaration-types';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';

const useFormUtils = (declarationIdOverride?: string) => {
    const { declaration } = useDeclarations();
    const { options: submissionFormTemplate } = useTemplateContext();
    const {
        country: productTemplateCountry,
        internalType: productTemplateInternalType,
        type: productTemplateFormType,
        declarationId: routeDeclarationId,
    } = useParams();

    const declarationId = declarationIdOverride ?? routeDeclarationId;

    const country = useMemo(
        () =>
            submissionFormTemplate?.country ??
            productTemplateCountry?.toUpperCase() ??
            (declarationId &&
            (declaration?.declarationExternalEntity === 'CDS' || declaration?.declarationExternalEntity === 'HMRC')
                ? 'UK'
                : declaration?.declarationExternalEntity === 'ICS2'
                ? 'EU'
                : 'IRELAND'),
        [declarationId, declaration, submissionFormTemplate?.country, productTemplateCountry]
    );

    const internalType = useMemo(() => {
        const internalType =
            submissionFormTemplate?.declarationType ??
            productTemplateInternalType ??
            (declarationId && declaration?.declarationInternalType);

        return internalType === 'IMPORT_NEW' ? 'IMPORT' : internalType;
    }, [
        declarationId,
        declaration?.declarationInternalType,
        productTemplateInternalType,
        submissionFormTemplate?.declarationType,
    ]);

    const externalEntity = useMemo(
        () =>
            (declarationId && declaration?.declarationExternalEntity) ??
            (submissionFormTemplate &&
                (submissionFormTemplate?.country === 'UK'
                    ? submissionFormTemplate?.declarationType === 'NCTS'
                        ? 'HMRC'
                        : 'CDS'
                    : submissionFormTemplate?.country === 'EU'
                    ? 'ICS2'
                    : 'REVENUE')) ??
            (productTemplateCountry === 'uk' ? (internalType === 'NCTS' ? 'HMRC' : 'CDS') : 'REVENUE'),
        [
            declarationId,
            declaration?.declarationExternalEntity,
            submissionFormTemplate,
            productTemplateCountry,
            internalType,
        ]
    );

    const declarationFormType = useMemo(() => {
        if (declaration?.irelandH7ImportDeclaration) return 'H7';
        if (declaration?.entrySummaryDeclaration) return 'ENS';
        if (declaration?.ieArrivalAtExitDeclaration) return 'ARRIVAL';

        return (
            declaration?.cdsDeclaration?.messageType ??
            declaration?.irelandImportDeclaration?.messageType ??
            declaration?.ieExportDeclaration?.messageType ??
            declaration?.gvmsDeclaration?.messageType ??
            declaration?.ieImportTemporaryStorageDeclaration?.declaration?.msgType ??
            declaration?.ieNctsDeclaration?.messageType ??
            declaration?.ukNctsDeclaration?.messageType ??
            declaration?.ieImportElectronicTransportDocument?.declaration?.messageType ??
            declaration?.ics2ENSDeclaration?.messageType
        );
    }, [declaration]);

    const formType = useMemo(
        () => submissionFormTemplate?.formType ?? productTemplateFormType ?? (declarationId && declarationFormType),
        [declarationId, declarationFormType, submissionFormTemplate?.formType, productTemplateFormType]
    );

    const isCds = country === 'UK' && internalType !== 'NCTS';
    const isAis = country === 'IRELAND' && internalType === 'IMPORT';
    const isAes = country === 'IRELAND' && internalType === 'EXPORT';
    const isArrival = country === 'IRELAND' && internalType === 'ARRIVAL';
    const isNcts = country === 'IRELAND' && internalType === 'NCTS';
    const isUkNcts = country === 'UK' && internalType === 'NCTS';
    const isCdsExportB1 = isCds && internalType === 'EXPORT' && formType === CDSExportMessageTypes.B1;
    const isAisH1 = isAis && formType === AISMessageTypes.H1;
    const isICS2 = externalEntity === DeclarationExternalEntity.ICS2;

    const formTypeForCds = `${country}_${internalType}_${formType}`;

    const countryLowerCase = useMemo(() => country.toLowerCase(), [country]);

    return {
        isAes,
        isCds,
        isCdsExportB1,
        isAis,
        isAisH1,
        isArrival,
        isNcts,
        isUkNcts,
        isICS2,
        formTypeForCds,
        internalType,
        formType,
        externalEntity,
        country,
        declarationFormType,
        countryLowerCase,
    };
};

export default useFormUtils;
